import type {
    AgAxisContextMenuActionEvent,
    AgCaptionContextMenuActionEvent,
    AgChartContextMenuEvent,
    AgChartLegendContextMenuEvent,
    AgContextMenuGetItemsParams,
    AgContextMenuGetItemsParamsAlways,
    AgContextMenuGetItemsParamsAxis,
    AgContextMenuGetItemsParamsCaption,
    AgContextMenuGetItemsParamsCrossLine,
    AgContextMenuGetItemsParamsLegendItem,
    AgContextMenuGetItemsParamsSeriesArea,
    AgContextMenuGetItemsParamsSeriesNode,
    AgContextMenuItem,
    AgContextMenuItemShowOn,
    AgContextMenuShowOnParams,
    AgCoordinates,
    AgCrossLineContextMenuActionEvent,
    AgNodeContextMenuActionEvent,
    AgSeriesAreaContextMenuActionEvent,
    ContextDefault,
    DatumDefault,
} from 'ag-charts-community';
import { _ModuleSupport, _Widget } from 'ag-charts-community';
import type { CallbackParamRules, DynamicContext } from 'ag-charts-core';
import {
    AbstractModuleInstance,
    callWithContext,
    clamp,
    createElement,
    getIconClassNames,
    toPlainText,
} from 'ag-charts-core';

import { ContextMenuItem, expandBuiltinLists, expandItems } from './contextMenuItem';
import { DEFAULT_CONTEXT_MENU_CLASS } from './contextMenuStyles';

type ContextShowOnMap = _ModuleSupport.ContextShowOnMap;
type ContextMenuEvent<K extends AgContextMenuItemShowOn = AgContextMenuItemShowOn> = _ModuleSupport.ContextMenuEvent<K>;
type ContextMenuCallback<K extends AgContextMenuItemShowOn = AgContextMenuItemShowOn> =
    _ModuleSupport.ContextMenuCallback<K>;

const { getItemId, ContextMenuRegistry } = _ModuleSupport;
type UnknownSeries = _ModuleSupport.ISeries<
    _ModuleSupport.SeriesNodeDatum,
    _ModuleSupport.SeriesProperties<object>,
    unknown
>;
type Caller = { context?: unknown } | undefined;

const moduleId = 'context-menu';

// `contextMenuRegistry` is optional on ChartRegistry, but this module's `register()` hook installs it
// before ContextMenu can be instantiated, so narrow it once here rather than asserting `!`.
export type ContextMenuCtx = Omit<DynamicContext<_ModuleSupport.ChartRegistry>, 'contextMenuRegistry'> & {
    readonly contextMenuRegistry: _ModuleSupport.ContextMenuRegistry;
};

const DATUM_KEYS = [
    'angleKey',
    'calloutLabelKey',
    'colorKey',
    'labelKey',
    'radiusKey',
    'sectorLabelKey',
    'sizeKey',
    'xKey',
    'yKey',
] as const satisfies readonly (keyof AgContextMenuGetItemsParamsSeriesNode)[];

type PickedNode = _ModuleSupport.SeriesNodeDatum & {
    [K in (typeof DATUM_KEYS)[number]]?: string;
} & {
    binIndex?: number;
    binRange?: [number, number];
    aggregatedValue?: number;
    frequency?: number;
};

type ShowOnParams = AgContextMenuShowOnParams<DatumDefault, ContextDefault>;
type SeriesNodeParams = Extract<ShowOnParams, { showOn: 'series-node' }>;
type AxisParams = Extract<ShowOnParams, { showOn: 'axis' }>;
type CrossLineParams = Extract<ShowOnParams, { showOn: 'cross-line' }>;
type CaptionParams = Extract<ShowOnParams, { showOn: 'caption' }>;
type LegendItemParams = Extract<ShowOnParams, { showOn: 'legend-item' }>;
type GetItemsOpts = {
    defaultItems: AgContextMenuItem[];
    active: ReadonlySet<AgContextMenuItemShowOn>;
    coordinates: AgCoordinates | undefined;
};
type GetItemsParams = [AgContextMenuGetItemsParams, Caller[]];

export class ContextMenu extends AbstractModuleInstance {
    private get opts() {
        return this.ctx.chartState.getValue('options', 'contextMenu') ?? {};
    }

    // Module context
    private readonly interactionManager: _ModuleSupport.InteractionManager;

    // State
    private pickedNodes?: ContextShowOnMap['series-node']['context'];
    private pickedLegendItem?: _ModuleSupport.CategoryLegendDatum;
    private pickedCaptionCtx?: ContextShowOnMap['caption']['context'];
    private pickedAxisCtx?: _ModuleSupport.AxisValuePick;
    private pickedCrossLine?: ContextShowOnMap['cross-line']['context'];
    private x: number = 0;
    private y: number = 0;
    private collapsingSubMenus = 0;

    // HTML elements
    private readonly element: HTMLElement;
    private readonly menuWidget: _Widget.MenuWidget = new _Widget.MenuWidget();
    private readonly mutationObserver?: MutationObserver;

    constructor(readonly ctx: ContextMenuCtx) {
        super();

        // Module context
        this.interactionManager = ctx.interactionManager;

        // State
        this.element = ctx.domManager.addChild('canvas-overlay', moduleId);
        this.element.classList.add(DEFAULT_CONTEXT_MENU_CLASS);
        this.element.style.display = 'none';
        this.element.addEventListener('contextmenu', (event) => event.preventDefault());
        // Automatically close the context menu when focus changes with TAB / Shift+TAB.
        this.element.addEventListener('focusout', ({ relatedTarget }) => {
            if (this.collapsingSubMenus > 0) return;
            if (relatedTarget == null || (relatedTarget instanceof Node && !this.element.contains(relatedTarget))) {
                this.hide();
            }
        });
        this.cleanup.register(
            () => this.element.remove(),
            () => this.menuWidget.destroy(),
            ctx.eventsHub.on('dom:hidden', () => this.hide()),
            this.menuWidget.addListener('collapse-widget', () => this.onCollapse())
        );
        this.menuWidget.addClass(`${DEFAULT_CONTEXT_MENU_CLASS}__menu`);

        if (typeof MutationObserver !== 'undefined') {
            const observer = new MutationObserver(() => {
                if (this.element.contains(this.menuWidget.getElement())) {
                    this.reposition();
                }
            });
            observer.observe(this.element, { childList: true });
            this.mutationObserver = observer;
            this.cleanup.register(() => observer.disconnect());
        }

        this.ctx.contextMenuRegistry.builtins.items['download'].action = () => {
            const title = ctx.chartService.title;
            let fileName = 'image';
            if (title?.enabled) {
                fileName = title.node.getPlainText().replace(/\.+/, '');
            }
            this.ctx.chartService.publicApi?.download({ fileName }).catch((e) => {
                this.ctx.logger.error('Unable to download chart', e);
            });
        };

        this.cleanup.register(
            this.ctx.eventsHub.on('context-menu:complete', (e) => this.onContext(e)),
            this.ctx.eventsHub.on('layout:complete', () => this.updateAxisInteraction())
        );
    }

    private updateAxisInteraction() {
        const enabled = this.opts.enabled ?? true;
        this.ctx.eventsHub.emit('axis-interaction:update', {
            source: moduleId,
            enabled,
            enableDoubleClick: false,
            enableDragging: false,
            enableScrolling: false,
            enableContextMenu: enabled,
        });
    }

    private axisRegion(pick: _ModuleSupport.AxisValuePick): CallbackParamRules<AxisParams> {
        const { axisId, boundSeries, direction, domain, value, index, depth, groupPercentage } = pick;
        return { showOn: 'axis', axisId, boundSeries, direction, domain, value, index, depth, groupPercentage };
    }

    private crossLineRegions(picks: ContextShowOnMap['cross-line']['context']): CrossLineParams[] {
        const result: CrossLineParams[] = [];
        for (const pick of picks) {
            const { crossLineId, axisId, direction, crossLineType, value, range } = pick;
            result.push({ showOn: 'cross-line', crossLineId, axisId, direction, crossLineType, value, range });
        }
        return result;
    }

    // Scopes that can overlap the series area: the area itself, an axis positioned inside it (e.g. `crossAt`),
    // and a cross line. These appear both as a primary and as a non-primary overlap, so they live here.
    private plotOverlapRegions(active: ReadonlySet<AgContextMenuItemShowOn>): ShowOnParams[] {
        const params: ShowOnParams[] = [];
        if (active.has('series-area')) params.push({ showOn: 'series-area' });
        if (active.has('axis') && this.pickedAxisCtx != null) params.push(this.axisRegion(this.pickedAxisCtx));
        if (active.has('cross-line') && this.pickedCrossLine != null) {
            params.push(...this.crossLineRegions(this.pickedCrossLine));
        }
        return params;
    }

    private makeGetItemsParams(event: ContextMenuEvent, active: ReadonlySet<AgContextMenuItemShowOn>): GetItemsParams {
        const { showOn } = event;
        const items = this.opts.items ?? ['defaults'];
        const opts: GetItemsOpts = {
            defaultItems: expandBuiltinLists(active, items, this.ctx.contextMenuRegistry),
            coordinates: this.ctx.chartService.toAgCoordinates(event),
            active,
        };
        // Agents: These params will be passed into user-options getItems() callback. makeGetItemsParams*() must use
        // `CallbackParamRules` to ensure that these params comply with the user API contract.
        switch (showOn) {
            case 'always':
                return this.makeGetItemsParamsAlways(opts);
            case 'series-area':
                return this.makeGetItemsParamsSeriesArea(opts);
            case 'series-node':
                return this.makeGetItemsParamsSeriesNode(opts);
            case 'axis':
                return this.makeGetItemsParamsAxis(opts);
            case 'cross-line':
                return this.makeGetItemsParamsCrossLine(opts);
            case 'caption':
                return this.makeGetItemsParamsCaption(opts);
            case 'legend-item':
                return this.makeGetItemsParamsLegendItem(opts);
            default:
                return showOn satisfies never; // unreachable
        }
    }

    private makeGetItemsParamsAlways(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems, active } = opts;
        const params: CallbackParamRules<AgContextMenuGetItemsParamsAlways<unknown, unknown>> = {
            showOn: 'always',
            defaultItems,
            coordinates: undefined,
            allShowOnParams: this.plotOverlapRegions(active),
        };
        const callers: Caller[] = [this.ctx.chartService];
        return [params, callers];
    }

    private makeGetItemsParamsSeriesArea(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems, active, coordinates } = opts;
        const params: CallbackParamRules<AgContextMenuGetItemsParamsSeriesArea<unknown, unknown>> = {
            showOn: 'series-area',
            defaultItems,
            coordinates,
            allShowOnParams: this.plotOverlapRegions(active),
        };
        const callers: Caller[] = [this.ctx.chartService];
        return [params, callers];
    }

    private makeGetItemsParamsSeriesNode(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems, active, coordinates } = opts;
        if (this.pickedNodes == null) throw new Error(`this.pickedNodes is null`);
        const regions = this.pickedNodes.map((node: PickedNode): SeriesNodeParams => {
            // FIXME: Some optional keys like dataIdKey are not set. Is that a concern?
            const itemId = getItemId(node, node.series.data?.dataIdKey);
            const region: SeriesNodeParams = {
                showOn: 'series-node',
                seriesId: node.series.id,
                itemId,
                datum: node.datum,
                selectionState: node.series.getSelectionStateString(node.datumIndex),
                isCollapsed: node.series.getCollapsedState(itemId),
            };

            for (const k of DATUM_KEYS) {
                if (node[k] !== undefined) {
                    region[k] = node[k];
                }
            }

            // Histogram bins carry standardised bin metadata; binIndex is always set for histogram nodes.
            if (node.binIndex !== undefined) {
                const { datums, binIndex, binRange, aggregatedValue, frequency } = node;
                Object.assign(region, { datums, binIndex, binRange, aggregatedValue, frequency });
            }
            return region;
        });
        if (regions.length === 0) throw new Error(`this.pickedNodes is empty`);

        // The topmost node (hit-test order) wins. Nodes overlapping it at this contextmenu point are broadcast in
        // the allShowOnParams property.
        const allShowOnParams: ShowOnParams[] = [...this.plotOverlapRegions(active), ...regions];
        // FIXME: params should be of type CallbackParamRules<AgContextMenuGetItemsParamsSeriesNode>
        const params: AgContextMenuGetItemsParamsSeriesNode = {
            ...regions[0],
            defaultItems,
            coordinates,
            allShowOnParams,
        };
        const callers: Caller[] = [this.pickedNodes[0].series.properties, this.ctx.chartService];
        return [params, callers];
    }

    private makeGetItemsParamsAxis(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems, active } = opts;
        if (this.pickedAxisCtx == null) throw new Error(`this.pickedAxisCtx is null`);
        const region = this.axisRegion(this.pickedAxisCtx);
        const allShowOnParams: ShowOnParams[] = [region];
        if (active.has('series-area')) allShowOnParams.push({ showOn: 'series-area' });
        if (active.has('cross-line') && this.pickedCrossLine != null) {
            allShowOnParams.push(...this.crossLineRegions(this.pickedCrossLine));
        }
        const params: CallbackParamRules<AgContextMenuGetItemsParamsAxis<DatumDefault, ContextDefault>> = {
            ...region,
            defaultItems,
            coordinates: undefined,
            allShowOnParams,
        };
        const callers: Caller[] = [this.pickedAxisCtx.caller, this.ctx.chartService];
        return [params, callers];
    }

    private makeGetItemsParamsCrossLine(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems, active, coordinates } = opts;
        if (this.pickedCrossLine == null) throw new Error(`this.pickedCrossLine is null`);
        const regions = this.crossLineRegions(this.pickedCrossLine);
        if (regions.length === 0) throw new Error(`this.pickedCrossLine is empty`);

        // The first crossline (rendering order) wins. Overlapping crosslines at this contextmenu point will be
        // broadcast in the allShowOnParams property.
        const allShowOnParams: ShowOnParams[] = [...regions];
        if (active.has('series-area')) allShowOnParams.push({ showOn: 'series-area' });
        if (active.has('axis') && this.pickedAxisCtx != null) allShowOnParams.push(this.axisRegion(this.pickedAxisCtx));
        const params: CallbackParamRules<AgContextMenuGetItemsParamsCrossLine<DatumDefault, ContextDefault>> = {
            showOn: 'cross-line',
            axisId: regions[0].axisId,
            crossLineId: regions[0].crossLineId,
            crossLineType: regions[0].crossLineType,
            direction: regions[0].direction,
            value: regions[0].value,
            range: regions[0].range,
            defaultItems,
            coordinates,
            allShowOnParams,
        };
        const callers: Caller[] = [this.ctx.chartService];
        return [params, callers];
    }

    private makeGetItemsParamsCaption(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems } = opts;
        const ctx = this.pickedCaptionCtx;
        if (ctx == null) throw new Error(`this.pickedCaptionCtx is null`);
        const region: CaptionParams = { showOn: 'caption', captionType: ctx.captionType, text: ctx.text };
        const params: CallbackParamRules<AgContextMenuGetItemsParamsCaption<DatumDefault, ContextDefault>> = {
            ...region,
            defaultItems,
            coordinates: undefined,

            allShowOnParams: [region],
        };
        const callers: Caller[] = [this.ctx.chartService];
        return [params, callers];
    }

    private makeGetItemsParamsLegendItem(opts: GetItemsOpts): GetItemsParams {
        const { defaultItems } = opts;
        if (this.pickedLegendItem == null) throw new Error(`this.pickedLegendItem is null`);
        const { itemId, seriesId, label, enabled } = this.pickedLegendItem;
        const text = toPlainText(label.text);
        const region: LegendItemParams = {
            showOn: 'legend-item',
            itemId,
            seriesId,
            text,
            visible: enabled,
        };
        const params: CallbackParamRules<AgContextMenuGetItemsParamsLegendItem<DatumDefault, ContextDefault>> = {
            ...region,
            defaultItems,
            coordinates: undefined,
            allShowOnParams: [region],
        };
        const callers: Caller[] = [this.ctx.chartService];
        return [params, callers];
    }

    private expandItemsOptions(event: ContextMenuEvent): ContextMenuItem[] {
        const result: ContextMenuItem[] = [];
        const opts = this.opts;
        const active = new Set(event.regions);

        let items: readonly Readonly<AgContextMenuItem>[] | undefined;
        if (opts.getItems) {
            const [params, callers]: GetItemsParams = this.makeGetItemsParams(event, active);
            items = callWithContext(callers, opts.getItems, params);
        }
        items ??= opts.items ?? ['defaults'];

        expandItems(active, this.ctx.contextMenuRegistry, items, result);

        return result;
    }

    private onContext(event: ContextMenuEvent) {
        if (!(this.opts.enabled ?? true)) return;

        event.widgetEvent.sourceEvent.preventDefault();
        this.x = event.canvasX;
        this.y = event.canvasY;

        // Regions can overlap (e.g. a datum node over a crossing axis), so populate every picked context the
        // event carries rather than a single mutually-exclusive one; item actions route by their own showOn.
        const { contexts } = event;
        this.pickedNodes = contexts['series-node'];
        this.pickedAxisCtx = contexts.axis;
        this.pickedCaptionCtx = contexts.caption;
        this.pickedLegendItem = contexts['legend-item']?.legendItem;
        this.pickedCrossLine = contexts['cross-line'];

        const expandedItems = this.expandItemsOptions(event);
        if (expandedItems.length === 0) return;

        this.show(event, expandedItems);
    }

    private show(event: ContextMenuEvent, expandedItems: ContextMenuItem[]) {
        const { sourceEvent } = event.widgetEvent;
        this.interactionManager.pushState(_ModuleSupport.InteractionState.ContextMenu);
        this.element.style.display = 'block';

        const overrideFocusVisible = sourceEvent.pointerType === 'touch' ? false : undefined;
        if (overrideFocusVisible !== undefined) {
            this.ctx.chartService.overrideFocusVisible(overrideFocusVisible);
        }

        this.createMenu(event, expandedItems);
        this.element.appendChild(this.menuWidget.getElement());
        this.menuWidget.expand({ sourceEvent, overrideFocusVisible });
    }

    private hide() {
        this.menuWidget.collapse();
    }

    private onCollapse() {
        this.interactionManager.popState(_ModuleSupport.InteractionState.ContextMenu);
        this.menuWidget.getElement().remove();
        this.element.style.display = 'none';
    }

    private onSubMenuExpand(button: _Widget.MenuItemWidget, menu: _Widget.MenuWidget) {
        const bounds = button.getBounds();
        button.setFocusOverride(true);
        button.getElement().insertAdjacentElement('afterend', menu.getElement());
        menu.getElement().style.position = 'absolute';

        const { isRtl } = this.ctx.domManager;
        const canvasRect = this.ctx.domManager.getBoundingClientRect();
        const buttonClientRect = button.getBoundingClientRect();
        const remainingSpaceOnRight = canvasRect.right - buttonClientRect.right;
        const remainingSpaceOnLeft = buttonClientRect.left - canvasRect.left;
        const { offsetWidth: menuOffsetWidth, offsetHeight: menuOffsetHeight } = menu.getElement();

        let y: number = bounds.y;
        // Clip the Y-position (if the submenu fits in the canvas).
        if (canvasRect.height > menuOffsetHeight) {
            const remainingSpaceOnBottom = canvasRect.bottom - buttonClientRect.top;
            if (remainingSpaceOnBottom < menuOffsetHeight) {
                y -= menuOffsetHeight - remainingSpaceOnBottom;
            }
        }

        const preferredSide = isRtl ? 'left' : 'right';
        const preferredSpace = preferredSide === 'right' ? remainingSpaceOnRight : remainingSpaceOnLeft;

        if (preferredSpace >= menuOffsetWidth) {
            // Preferred-side popout
            const x = preferredSide === 'right' ? bounds.x + bounds.width : bounds.x - menuOffsetWidth;
            menu.setBounds({ x, y });
        } else {
            // Fallback-side popout
            const x = preferredSide === 'right' ? bounds.x - menuOffsetWidth : bounds.x + bounds.width;
            const delta = (preferredSide === 'right' ? remainingSpaceOnLeft : remainingSpaceOnRight) - menuOffsetWidth;
            if (delta >= 0) {
                menu.setBounds({ x, y });
            } else {
                // Clipped to the edge of the canvas
                menu.setBounds({ x: x + (preferredSide === 'right' ? -delta : delta), y });
            }
        }
    }
    private onSubMenuCollapse(button: _Widget.MenuItemWidget, menu: _Widget.MenuWidget) {
        button.setFocusOverride(undefined);
        // Removing HTML elements can fire a 'focusout' with `relatedTarget: null`, which would dismiss the whole
        // context menu.
        this.collapsingSubMenus++;
        menu.remove();
        this.collapsingSubMenus--;
    }

    private createMenu(event: ContextMenuEvent, expandedItems: ContextMenuItem[]) {
        const { menuWidget } = this;
        menuWidget.clear();
        menuWidget.setTabIndex(-1);
        this.createMenuItems(event, menuWidget, expandedItems);
    }

    private createMenuItems(event: ContextMenuEvent, menuWidget: _Widget.MenuWidget, expandedItems: ContextMenuItem[]) {
        for (const item of expandedItems) {
            switch (item.type) {
                case 'separator': {
                    const sep = menuWidget.addSeparator();
                    sep.classList.add(`${DEFAULT_CONTEXT_MENU_CLASS}__divider`);
                    break;
                }
                case 'action': {
                    if (item.items.length === 0) {
                        const btn = new _Widget.MenuItemWidget();
                        this.initButtonElement(event, btn, item);
                        menuWidget.addChild(btn);
                    } else {
                        const { subMenuButton, subMenu } = menuWidget.addSubMenu();
                        subMenu.addClass(`${DEFAULT_CONTEXT_MENU_CLASS}__menu`);
                        subMenu.addListener('expand-widget', () => this.onSubMenuExpand(subMenuButton, subMenu));
                        subMenu.addListener('collapse-widget', () => this.onSubMenuCollapse(subMenuButton, subMenu));
                        this.initButtonElement(event, subMenuButton, item);
                        this.createMenuItems(event, subMenu, item.items);
                    }
                    break;
                }
                default:
                    throw new Error('unhandled case');
            }
        }
    }
    private createButtonOnClick(
        event: ContextMenuEvent,
        showOn: AgContextMenuItemShowOn,
        callback: ContextMenuCallback
    ): (event: _Widget.WidgetEvent) => void {
        const showEvent = event.widgetEvent.sourceEvent;
        // Agents: These params will be passed into user-options actions() callbacks. Returned functions must use
        // `CallbackParamRules` to ensure that these params comply with the user API contract.
        if (ContextMenuRegistry.checkCallback('legend-item', showOn, callback)) {
            return (menuItemClickEvent: _ModuleSupport.WidgetEvent) => {
                if (this.pickedLegendItem) {
                    const { seriesId, itemId, label } = this.pickedLegendItem;
                    const { chartService: chart } = this.ctx;
                    const series: UnknownSeries | undefined = chart.series.find((s) => s.id === seriesId);
                    const callers: Caller[] = [series?.properties, chart];
                    const apiEvent: CallbackParamRules<AgChartLegendContextMenuEvent> = {
                        type: 'contextmenu',
                        seriesId,
                        itemId,
                        text: toPlainText(label.text),
                        // FIXME: Shouldn't this be showEvent?
                        event: menuItemClickEvent.sourceEvent,
                    };
                    callWithContext(callers, callback, apiEvent);
                    this.hide();
                } else {
                    this.ctx.logger.error('legend item not found');
                }
            };
        } else if (ContextMenuRegistry.checkCallback('series-area', showOn, callback)) {
            return () => {
                const coordinates: AgCoordinates | undefined = this.ctx.chartService.toAgCoordinates(event);
                const caller: Caller = this.ctx.chartService;
                const apiEvent: CallbackParamRules<AgSeriesAreaContextMenuActionEvent> = {
                    type: 'seriesContextMenuAction',
                    event: showEvent,
                    coordinates,
                };
                callWithContext(caller, callback, apiEvent);
                this.hide();
            };
        } else if (ContextMenuRegistry.checkCallback('series-node', showOn, callback)) {
            return () => {
                const { chartService: chart } = this.ctx;
                const pickedNodes = this.pickedNodes;
                if (!pickedNodes?.[0]) return;

                const coordinates: AgCoordinates | undefined = this.ctx.chartService.toAgCoordinates(event);
                const callers: (Caller | undefined)[] = [pickedNodes[0].series.properties, chart];
                // FIXME: apiEvent should be of type CallbackParamRules<AgNodeContextMenuActionEvent>
                const apiEvent: AgNodeContextMenuActionEvent | undefined =
                    pickedNodes[0]?.series.createNodeContextMenuActionEvent({
                        event: showEvent,
                        datums: pickedNodes,
                        winner: 0,
                        coordinates,
                    });
                if (apiEvent) {
                    callWithContext(callers, callback, apiEvent);
                } else {
                    this.ctx.logger.error('series node not found');
                }
                this.hide();
            };
        } else if (ContextMenuRegistry.checkCallback('axis', showOn, callback)) {
            return () => {
                if (this.pickedAxisCtx) {
                    const { axisId, direction, boundSeries, domain, value, index, depth, groupPercentage } =
                        this.pickedAxisCtx;
                    const callers: Caller[] = [this.pickedAxisCtx.caller, this.ctx.chartService];
                    const apiEvent: CallbackParamRules<AgAxisContextMenuActionEvent<never>> = {
                        type: 'axisContextMenuAction',
                        event: showEvent,
                        axisId,
                        direction,
                        boundSeries,
                        domain,
                        value,
                        index,
                        depth,
                        groupPercentage,
                    };
                    callWithContext(callers, callback, apiEvent);
                } else {
                    this.ctx.logger.error('axis item not found');
                }
                this.hide();
            };
        } else if (ContextMenuRegistry.checkCallback('cross-line', showOn, callback)) {
            return () => {
                if (this.pickedCrossLine && this.pickedCrossLine.length > 0) {
                    const { crossLineId, axisId, direction, crossLineType, value, range } = this.pickedCrossLine[0];
                    const coordinates: AgCoordinates | undefined = this.ctx.chartService.toAgCoordinates(event);
                    const callers: Caller = this.ctx.chartService;
                    const apiEvent: CallbackParamRules<AgCrossLineContextMenuActionEvent<never>> = {
                        type: 'crossLineContextMenuAction',
                        event: showEvent,
                        coordinates,
                        crossLineId,
                        axisId,
                        direction,
                        crossLineType,
                        value,
                        range,
                    };
                    callWithContext(callers, callback, apiEvent);
                } else {
                    this.ctx.logger.error('cross line item not found');
                }
                this.hide();
            };
        } else if (ContextMenuRegistry.checkCallback('caption', showOn, callback)) {
            return () => {
                if (this.pickedCaptionCtx) {
                    const { captionType, text } = this.pickedCaptionCtx;
                    const callers: Caller = this.ctx.chartService;
                    const apiEvent: CallbackParamRules<AgCaptionContextMenuActionEvent<never>> = {
                        type: 'captionContextMenuAction',
                        captionType,
                        text,
                        event: showEvent,
                    };
                    callWithContext(callers, callback, apiEvent);
                } else {
                    this.ctx.logger.error('caption item not found');
                }
                this.hide();
            };
        } else {
            return () => {
                const caller: Caller = this.ctx.chartService;
                const coordinates: AgCoordinates | undefined = this.ctx.chartService.toAgCoordinates(event);
                const apiEvent: CallbackParamRules<AgChartContextMenuEvent> = {
                    type: 'contextMenuEvent',
                    event: showEvent,
                    coordinates,
                };
                // Use `satisfies` to check that all other callback types (those with additional context-based parameters)
                // have been accounted for.
                callWithContext(caller, callback satisfies ContextMenuCallback<'always'>, apiEvent);
                this.hide();
            };
        }
    }

    private initTableCells(elem: Element) {
        const cellIcon = createElement('div');
        const cellLabel = createElement('div');
        const cellArrow = createElement('div');
        cellIcon.classList.toggle(`${DEFAULT_CONTEXT_MENU_CLASS}__icon`, true);
        cellLabel.classList.toggle(`${DEFAULT_CONTEXT_MENU_CLASS}__cell`, true);
        cellArrow.classList.toggle(`${DEFAULT_CONTEXT_MENU_CLASS}__cell`, true);
        cellIcon.ariaHidden = 'true';
        cellLabel.role = 'presentation';
        cellArrow.ariaHidden = 'true';
        elem.append(cellIcon, cellLabel, cellArrow);
        return { cellIcon, cellLabel, cellArrow };
    }

    private initButtonElement(event: ContextMenuEvent, button: _Widget.MenuItemWidget, item: ContextMenuItem) {
        button.addClass(`${DEFAULT_CONTEXT_MENU_CLASS}__item`);
        button.setEnabled(item.enabled);
        const label = this.ctx.localeManager.t(item.label);

        const cellPaddingClass = `${DEFAULT_CONTEXT_MENU_CLASS}__cellpadding`;
        const { cellIcon, cellLabel, cellArrow } = this.initTableCells(button.getElement());
        cellLabel.textContent = label;
        cellLabel.classList.add(cellPaddingClass);
        if (item.iconUrl != null) {
            const img = createElement('img');
            img.src = item.iconUrl;
            cellIcon.append(img);
            cellIcon.classList.add(cellPaddingClass);
        }
        if (item.items.length > 0) {
            const span = createElement('span', getIconClassNames('chevron-right'));
            cellArrow.append(span);
            cellArrow.classList.add(cellPaddingClass);
        }

        const { showOn, action } = item;
        if (action != null) {
            button.addListener('click', this.createButtonOnClick(event, showOn, action));
        }
        if (item.items.length === 0) {
            // TODO: move this logic into MenuWidget
            button.addListener('mouseleave', () => button.setFocusOverride(false));
            button.addListener('mouseenter', () => button.setFocusOverride(undefined));
        }
    }

    private reposition() {
        const { isRtl } = this.ctx.domManager;
        let { x, y } = this;

        this.element.style.top = 'unset';
        this.element.style.bottom = 'unset';

        const canvasRect = this.ctx.domManager.getBoundingClientRect();
        const { offsetWidth: width, offsetHeight: height } = this.element;

        x = clamp(0, isRtl ? x - width : x, canvasRect.width - width);
        y = clamp(0, y, canvasRect.height - height);

        this.element.style.left = `${x}px`;
        this.element.style.top = `calc(${y}px - 0.5em)`;
    }

    public override destroy() {
        super.destroy();

        this.mutationObserver?.disconnect();

        this.ctx.domManager.removeStyles(moduleId);
        this.ctx.domManager.removeChild('canvas-overlay', moduleId);
    }
}
